/**
 * DossierHeroV2 — hero cockpit
 * ────────────────────────────
 * Une seule mission : raconter en un coup d'œil de quoi parle ce dossier.
 *
 *  • Photo immersive du véhicule (colonne gauche, plein pied)
 *  • Nom du véhicule (H1)
 *  • Client + bon de commande + progression % (méta serrée)
 *  • Progression Tesla 13-segments interactive (survol → tooltip label)
 *
 * Toutes les briques visuelles proviennent de wyvio-ui.
 * Aucune couleur ni typo hors barème DESIGN_SYSTEM.
 */
import { useMemo } from "react";
import { Hash, User, Building2 } from "lucide-react";
import { WorkflowProgress, MicroLabel } from "@/components/wyvio-ui";
import { STEP_STATUS } from "@/data/mockData";

const DossierHeroV2 = ({ vehicle, onStepClick }) => {
  const steps = vehicle.steps || [];
  const activeIndex = useMemo(() => {
    const idx = steps.findIndex((s) => s.status === STEP_STATUS.IN_PROGRESS);
    if (idx >= 0) return idx;
    const b = steps.findIndex((s) => s.status === STEP_STATUS.BLOCKED);
    if (b >= 0) return b;
    return steps.findIndex((s) => s.status !== STEP_STATUS.DONE);
  }, [steps]);

  return (
    <section
      data-testid="dossier-hero-v2"
      className="rounded-3xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,520px)_1fr]">
        {/* Photo — plein pied, ratio automobile */}
        <div className="relative aspect-[5/4] lg:aspect-auto lg:min-h-[380px] bg-[#F3F4F6] dark:bg-[#0A0A0A]">
          {vehicle.image ? (
            <img
              src={vehicle.image}
              alt={vehicle.name}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#D1D5DB] dark:text-[#3A3A3A]">
              <svg viewBox="0 0 64 48" className="h-24 w-24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M6 33 L10 22 C12 18 15 16 20 16 L44 16 C49 16 52 18 54 22 L58 33" />
                <path d="M4 33 H60 V38 C60 39 59 40 58 40 H52 C51 40 50 39 50 38 V37 H14 V38 C14 39 13 40 12 40 H6 C5 40 4 39 4 38 Z" />
                <circle cx="17" cy="37" r="3" />
                <circle cx="47" cy="37" r="3" />
              </svg>
            </div>
          )}
        </div>

        {/* Identité */}
        <div className="p-8 lg:p-10 flex flex-col">
          <div className="flex items-center gap-2 text-[11.5px] font-medium">
            <Hash size={11} strokeWidth={2.25} className="text-[#9CA3AF] dark:text-[#525252]" />
            <span
              data-testid="dossier-order-number"
              className="tabular-nums tracking-wide text-[#0A0A0A] dark:text-white"
            >
              {vehicle.orderNumber}
            </span>
          </div>

          <h1
            data-testid="dossier-title"
            className="mt-4 text-[38px] sm:text-[46px] lg:text-[54px] leading-[0.98] font-semibold tracking-[-0.035em] text-[#0A0A0A] dark:text-white"
          >
            {vehicle.name}
          </h1>

          {/* Client + commercial */}
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <User size={13} strokeWidth={2} className="text-[#9CA3AF] dark:text-[#525252]" />
              <span
                data-testid="dossier-client-name"
                className="text-[14px] font-medium text-[#0A0A0A] dark:text-white"
              >
                {vehicle.client}
              </span>
            </div>
            {vehicle.commercial && (
              <div className="flex items-center gap-2">
                <Building2 size={13} strokeWidth={2} className="text-[#9CA3AF] dark:text-[#525252]" />
                <span className="text-[14px] text-[#6B7280] dark:text-[#A3A3A3]">
                  {vehicle.commercial}
                </span>
              </div>
            )}
          </div>

          {/* Progression */}
          <div className="mt-auto pt-10">
            <div className="flex items-baseline justify-between">
              <MicroLabel>Progression</MicroLabel>
              <span
                data-testid="dossier-progress-value"
                className="text-[32px] font-semibold text-[#0A0A0A] dark:text-white tabular-nums leading-none"
              >
                {vehicle.progress}
                <span className="text-[15px] text-[#9CA3AF] dark:text-[#525252] ml-0.5">
                  %
                </span>
              </span>
            </div>
            <div className="mt-4">
              <InteractiveProgress
                steps={steps}
                activeIndex={activeIndex}
                onStepClick={onStepClick}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * WorkflowProgress cliquable — chaque segment ouvre la modale d'étape.
 * Basé sur WorkflowProgress mais avec buttons + tooltip natif.
 */
const InteractiveProgress = ({ steps = [], activeIndex, onStepClick }) => (
  <div
    data-testid="hero-progress-bar"
    className="flex items-center gap-1"
    aria-label="Progression interactive du dossier"
  >
    {steps.map((s, i) => {
      const done = s.status === "termine";
      const blocked = s.status === "bloque";
      const active = i === activeIndex && !done;
      const cls = blocked
        ? "bg-[#EF4444] hover:brightness-110"
        : done
        ? "bg-[#0A0A0A] dark:bg-white hover:opacity-80"
        : active
        ? "bg-[#C8FF2F] animate-pulse"
        : "bg-[#EEF2F5] dark:bg-[#262626] hover:bg-[#DCE1E7] dark:hover:bg-[#3A3A3A]";
      return (
        <button
          key={s.key || i}
          type="button"
          data-testid={`hero-progress-step-${s.key}`}
          title={s.label || s.title}
          onClick={() => onStepClick?.(s)}
          className={[
            "h-2 flex-1 rounded-full transition-all duration-200",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8FF2F] focus-visible:ring-offset-1",
            cls,
          ].join(" ")}
          aria-label={`${s.label || s.title} — ${s.status}`}
        />
      );
    })}
  </div>
);

export default DossierHeroV2;
